import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaImages, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { fetchPublishedAlbums } from '../lib/gallery';
import SAMPLE_GALLERY from '../data/sampleGallery';
import './Gallery.css';

const Gallery = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentAlbum, setCurrentAlbum] = useState(null);

  // The school's published albums; the sample photos until the first album is published (or if loading fails).
  const [galleryAlbums, setGalleryAlbums] = useState(null);

  useEffect(() => {
    let active = true;
    fetchPublishedAlbums()
      .then((albums) => { if (active) setGalleryAlbums(albums.length ? albums : SAMPLE_GALLERY); })
      .catch((err) => {
        console.error('Failed to load the gallery', err);
        if (active) setGalleryAlbums(SAMPLE_GALLERY);
      });
    return () => { active = false; };
  }, []);

  const handleCardClick = (album) => {
    setExpandedCard(expandedCard?.id === album.id ? null : album);
  };

  const handleImageClick = (album, imageIndex, e) => {
    e.stopPropagation();
    setCurrentAlbum(album);
    setCurrentImageIndex(imageIndex);
    setSelectedImage(album.images[imageIndex]);
  };

  const handleNextImage = useCallback(() => {
    if (currentAlbum && currentImageIndex < currentAlbum.images.length - 1) {
      const newIndex = currentImageIndex + 1;
      setCurrentImageIndex(newIndex);
      setSelectedImage(currentAlbum.images[newIndex]);
    }
  }, [currentAlbum, currentImageIndex]);

  const handlePrevImage = useCallback(() => {
    if (currentAlbum && currentImageIndex > 0) {
      const newIndex = currentImageIndex - 1;
      setCurrentImageIndex(newIndex);
      setSelectedImage(currentAlbum.images[newIndex]);
    }
  }, [currentAlbum, currentImageIndex]);

  const closeImageModal = useCallback(() => {
    setSelectedImage(null);
    setCurrentAlbum(null);
    setCurrentImageIndex(0);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (selectedImage) {
        if (e.key === 'Escape') {
          closeImageModal();
        } else if (e.key === 'ArrowRight') {
          handleNextImage();
        } else if (e.key === 'ArrowLeft') {
          handlePrevImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage, closeImageModal, handleNextImage, handlePrevImage]);

  return (
    <div className="gallery">
      <section className="gallery-hero page-hero">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Gallery
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Explore our vibrant school community through photos
          </motion.p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="gallery-intro">
            <p>
              Take a visual tour of our school facilities, classrooms, and activities. 
              See our students engaged in learning, playing, and growing in a nurturing environment.
            </p>
          </div>

          {galleryAlbums === null && <p className="gallery-loading" role="status">Loading photos...</p>}
          <div className="gallery-grid">
            {(galleryAlbums ?? []).map((album, index) => (
              <motion.div
                key={album.id}
                className={`gallery-item ${expandedCard?.id === album.id ? 'expanded' : ''}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: expandedCard?.id === album.id ? 1 : 1.05 }}
                onClick={() => handleCardClick(album)}
                {...(expandedCard?.id === album.id ? {} : {
                  role: 'button',
                  tabIndex: 0,
                  'aria-label': `${album.title}: open album (${album.images.length} photos)`,
                  onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCardClick(album);
                    }
                  }
                })}
              >
                {expandedCard?.id === album.id ? (
                  // Expanded view with all images
                  <motion.div 
                    className="gallery-expanded-view"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="expanded-header">
                      <h3>{album.title}</h3>
                      <button type="button" className="collapse-btn" aria-label="Close album" onClick={(e) => { e.stopPropagation(); setExpandedCard(null); }}>
                        <FaTimes aria-hidden="true" />
                      </button>
                    </div>
                    <div className="expanded-images-grid">
                      {album.images.map((img, imgIndex) => (
                        <motion.button
                          type="button"
                          key={img.id ?? imgIndex}
                          className="expanded-image-item"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: imgIndex * 0.05 }}
                          onClick={(e) => handleImageClick(album, imgIndex, e)}
                        >
                          <img src={img.src} alt={img.alt} loading="lazy" width={img.width} height={img.height} />
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  // Collapsed view with thumbnail
                  <>
                    <div className="gallery-image">
                      <img
                        src={album.images[album.coverIndex ?? 0].src}
                        alt={album.images[album.coverIndex ?? 0].alt}
                        loading="lazy"
                      />
                      <div className="image-count-badge" aria-hidden="true">
                        <FaImages /> {album.images.length}
                      </div>
                    </div>
                    <div className="gallery-overlay">
                      <h3>{album.title}</h3>
                      {album.category && <p className="category">{album.category}</p>}
                    </div>
                  </>
                )}
              </motion.div>
            ))}
          </div>

          <div className="gallery-note">
            <h3>Want to See More?</h3>
            <p>
              Schedule a campus tour to see our facilities in person and meet our wonderful 
              staff and students. We'd love to show you around!
            </p>
            <Link to="/contact?type=visit_request" className="btn btn-primary">Schedule a Tour</Link>
          </div>
        </div>
      </section>

      {/* Full Size Image Modal */}
      <AnimatePresence>
        {selectedImage && currentAlbum && (
          <motion.div
            className="image-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeImageModal}
          >
            <motion.div
              className="image-modal-content single-image"
              role="dialog"
              aria-modal="true"
              aria-label={`${currentAlbum.title}, photo ${currentImageIndex + 1} of ${currentAlbum.images.length}`}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="image-modal-close"
                onClick={closeImageModal}
                aria-label="Close photo"
                autoFocus
              >
                <FaTimes aria-hidden="true" />
              </button>

              {/* Previous Button */}
              {currentImageIndex > 0 && (
                <motion.button
                  type="button"
                  className="image-nav-btn prev-btn"
                  onClick={handlePrevImage}
                  aria-label="Previous photo"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaChevronLeft aria-hidden="true" />
                </motion.button>
              )}

              {/* Next Button */}
              {currentImageIndex < currentAlbum.images.length - 1 && (
                <motion.button
                  type="button"
                  className="image-nav-btn next-btn"
                  onClick={handleNextImage}
                  aria-label="Next photo"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaChevronRight aria-hidden="true" />
                </motion.button>
              )}

              <motion.img
                key={currentImageIndex}
                src={selectedImage.src}
                alt={selectedImage.alt}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              />

              {/* Image Counter */}
              <div className="image-counter">
                {currentImageIndex + 1} / {currentAlbum.images.length}
              </div>

              {/* Album Title */}
              <div className="image-info-bar">
                <h4>{currentAlbum.title}</h4>
                {selectedImage.caption && <p>{selectedImage.caption}</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
